import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import PrimaryButton from "../components/ui/PrimaryButton";
import { useAuth } from "../context/AuthContext";
import "./LoginPage.css";

const LoginPage: React.FC = () => {
  const { login, signup, isLoading, user, isReady } = useAuth();
  const navigate = useNavigate();

  // Default values from CloudFormation output for dev/demo purposes
  const cognitoDomain =
    process.env.REACT_APP_COGNITO_DOMAIN ||
    "https://earlyspot-demo-dev.auth.ap-southeast-2.amazoncognito.com";
  const cognitoClientId =
    process.env.REACT_APP_COGNITO_CLIENT_ID || "1qqi337eu5an8433mfcmpn3go6";
  const cognitoRedirectUri =
    process.env.REACT_APP_COGNITO_REDIRECT_URI ||
    `${window.location.origin}/oauth/callback`;

  const [mode, setMode] = useState<"login" | "signup">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isReady && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, isReady, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await signup(email, password, name || undefined);
      }
      navigate("/dashboard");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Something went wrong";
      setError(message);
    }
  };

  const handleGoogle = () => {
    if (!cognitoDomain || !cognitoClientId) {
      setError("Google login is not configured yet.");
      return;
    }
    const url = `${cognitoDomain}/oauth2/authorize?identity_provider=Google&redirect_uri=${encodeURIComponent(
      cognitoRedirectUri
    )}&response_type=token&client_id=${encodeURIComponent(
      cognitoClientId
    )}&scope=openid+profile+email`;
    window.location.href = url;
  };

  return (
    <div className="login">
      <Card padding="lg" className="login__card" elevated>
        <div className="login__header">
          <h2>{mode === "login" ? "Welcome back" : "Create an account"}</h2>
          <p>
            {mode === "login"
              ? "Log in to keep your scan history tied to your account."
              : "Sign up to save your scans across devices."}
          </p>
        </div>

        <form className="login__form" onSubmit={handleSubmit}>
          {mode === "signup" && (
            <label className="login__label">
              Name
              <input
                className="login__input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </label>
          )}
          <label className="login__label">
            Email
            <input
              className="login__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="login__label">
            Password
            <input
              className="login__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {error && <div className="login__error">{error}</div>}

          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? "Working..." : mode === "login" ? "Login" : "Sign up"}
          </PrimaryButton>
        </form>

        <div className="login__divider">or</div>
        <button className="login__google" type="button" onClick={handleGoogle}>
          Continue with Google
        </button>

        <div className="login__actions">
          <button
            className="login__link"
            type="button"
            onClick={() =>
              setMode((prev) => (prev === "login" ? "signup" : "login"))
            }
          >
            {mode === "login"
              ? "Need an account? Sign up"
              : "Already have an account? Log in"}
          </button>
          <button
            className="login__link"
            type="button"
            onClick={() => navigate("/dashboard")}
          >
            Continue as guest
          </button>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
